import React, { useState, useRef, useMemo } from "react";
import { GRID_COL_INDEX_ATTRIBUTE, Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@material-ui/core";
import { SimpleLoading } from "..";
import { profileIconDefault } from "../../../assets/logos/icons";
import SortableHeader from "../sortableHeader/sortableHeader.pure"; // Import the SortableHeader component
import "./gridHL.css"; // Ensure this CSS file includes the necessary styles
import CellActionsNew from "./CellActionsNew.pure";
import { useDimensions } from "../../../hooks";
import moment from "moment";
import { useTableKeyboardNavigation } from "@progress/kendo-react-data-tools";
import HeaderCell from "../headerCell/headerCell.pure";

const getValueNestedObject = (dataItem, field) => {
  const path = field.split(".")
  if (path.length === 1) return dataItem[field]

  let currentObj = dataItem
  for (const key of path) {
    currentObj = currentObj[key]
    if (!currentObj) return ""
  }
  return currentObj
}

const GridPure = (props) => {
  const {
    // miscellaneous props
    skip,
    take,
    dataGrid,
    loading,
    minHeight,
    styleGrid,
    changeSort,
    stateSortStart,
    iconAvatar, // if true, then add avatar to the first field
    fieldImg,
    //field names for the dataItems
    fieldNameCol1,
    // fieldSubtitles
    fieldSubtitleCol1,
    textSubtitle,
    rowsItems,
    actions,
    onArrowClick,
    footerCell,
    editField,
    onItemChange,
    setData,
    isDataDashboard
  } = props;
  const { t } = useTranslation();
  const gridRef = useRef(null)
  const { width: gridWidth } = useDimensions(gridRef);
  const [ascending, setAscending] = useState(true);
  const [selectedField, setSelectedField] = useState(null);
  const hideActionsTitle = useMediaQuery(`(max-width:820px)`);

  const initialDataState = {
    skip,
    take,
  };
  const [page, setPage] = useState(initialDataState);

  const onPageChange = (event) => {
    setPage(event.page);
  };

  const gridSignatureRequestPanelStyle = {
    minHeight: minHeight,
    fontFamily: "Quicksand",
    ...styleGrid,
  };

  const gridColumnNoRecordsStyle = {
    color: "#2C353C",
    fontSize: "16px",
    fontWeight: 550,
    fontFamily: "Quicksand",
  };

  const pagerSettings = {
    info: true,
    buttonCount: 3,
    type: "numeric",
    previousNext: true,
    pageSizes: false,
  }


  const headerStyles = {
    color: "#3B444D",
    fontSize: "14px",
    paddingTop: 10,
    paddingBottom: 10,
    fontWeight: "700",
    fontFamily: "Quicksand"
  };

  const columnStyles = {
    fontSize: "14px",
    fontWeight: "500",
    fontFamily: "Quicksand"
  };

  const CellActionsAux = (props) => {
    return <CellActionsNew
      actions={actions}
      dataItem={props.dataItem}
      onArrowClick={onArrowClick}
      {...props}
    />
  }

  const avatarCell = (props) => {
    return (
      <td>
        <div className={"gridHLPure-containerAvatar"}>
          {iconAvatar === true ? (
            <div className="gridHLPure-imageAvatar">
              <img
                alt="profile-img"
                src={props.dataItem[fieldImg] || profileIconDefault}
                className="gridHLPure-avatar"
              />
            </div>
          ) : (
            " "
          )}
          <div className={"gridHLPure-containerLabel"}>
            <span className="gridHLPure-userName">
              {props.dataItem[fieldNameCol1] || "----"}
            </span>
            {
              (fieldSubtitleCol1 && textSubtitle) ?
                <div className="gridHLPure-detailsContainer">
                  <span className="gridHLPure-fieldSubtitle">
                    {textSubtitle + props.dataItem[fieldSubtitleCol1]}
                  </span>
                </div>
                : (<div className="gridHLPure-detailsContainer">
                  <span className="gridHLPure-fieldSubtitle">
                    {props.dataItem[fieldSubtitleCol1]}
                  </span>
                </div>)
            }
          </div>
        </div>
      </td>
    );
  };


  const CustomCell = (props) => {
    const { dataItem, field, className, format, decimalPlaces } = props;
    const navigationAttributes = useTableKeyboardNavigation(props.id);

    const { value, style } = useMemo(() => {
      let value = getValueNestedObject(dataItem, field)
      let style = {}

      switch (format) {
        case "date":
          value = moment(value).format('L HH:mm:ss')
          break;
        case "currency":
          value = `$${parseFloat(value).toFixed(2)}`
          break;
        case "numeric":
          value = `${parseFloat(value).toFixed(decimalPlaces ?? 0)}`
          break;
      }

      return { value, style }
    }, [JSON.stringify(dataItem), field])

    return <td
      className={className}
      style={{ ...props.style, ...style }}
      colSpan={props.colSpan}
      role={"gridcell"}
      aria-colindex={props.ariaColumnIndex}
      aria-selected={props.isSelected}
      {...{
        [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex,
      }}
      {...navigationAttributes}
    >
      <span style={columnStyles}>{value}</span>
    </td>
  };

  const calcWith = (value) => {
    if (typeof value === 'string' && value.includes('%')) {
      const percentage = +value.split('%')[0]
      return gridWidth / 100 * percentage;
    }
    return value;
  };
  return loading ? (
    <SimpleLoading />
  ) : (
    <div ref={gridRef} className="gridHLPure-container" style={{ width: '100%', overflow: 'hidden' }}>
      <Grid
        className={`gridHLPure ${props.className ? props.className : ''}`}
        data={dataGrid?.slice(page.skip, page.take + page.skip)}
        reorderable
        style={gridSignatureRequestPanelStyle}
        skip={page.skip}
        take={page.take}
        pageable={pagerSettings}
        total={dataGrid.length}
        onPageChange={onPageChange}
        editField={editField}
        onItemChange={onItemChange}
      >
        {
          rowsItems && rowsItems.map((item) => {
            const { sorteable, avatar, actions, locked, decimalPlaces, editable = false, width, editor, headerClassName } = item

            if (editField) {
              return <GridColumn field={item.field} title={item.title} width={width} editable={editable} editor={editor} />
            }

            return <GridColumn
              field={item.field}
              title={actions ? hideActionsTitle ? '' : item.title : item.title}
              headerClassName={headerClassName}
              className={item.className}
              width={item.width ? calcWith(item.width) : undefined}
              locked={locked}
              cell={avatar ? avatarCell : !actions ? (props) => <CustomCell {...props} decimalPlaces={decimalPlaces} /> : null}
              cells={actions && { data: CellActionsAux }}
              format={item.format}
              headerCell={(props) => (
                <HeaderCell dataGrid={dataGrid} setData={setData} ascending={ascending} setAscending={setAscending} selectedField={selectedField} setSelectedField={setSelectedField} {...props} isDataDashboard={isDataDashboard} sorteable={sorteable} headerStyles={headerStyles} />
              )}
              footerCell={footerCell}
              sortState={sorteable && stateSortStart}
              onSortChange={() => sorteable && changeSort(stateSortStart)} />
          })
        }

        <GridNoRecords>
          <span style={gridColumnNoRecordsStyle}>
            {t("configurationDomainPanel.notResults")}
          </span>
        </GridNoRecords>
      </Grid>
    </div>
  );
};

export default GridPure;
